import { Suspense, useCallback } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  CalendarCheck,
  CalendarDays,
  History,
  LogOut,
  Moon,
  Pill,
  Settings,
  Sun,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { cn } from '@/lib/cn';
import { api } from '@/lib/api';
import { useTheme } from '@/lib/useTheme';
import { useSession } from '@/lib/session';
import { IconButton } from '@/components/ui/icon-button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { ProfileSwitcher } from './profile-switcher';

/**
 * The application chrome.
 *
 * Two navigations, one route table:
 *   - lg and up: a persistent sidebar, because a desktop has the width and a
 *     bottom bar on a 27" monitor is absurd.
 *   - below lg: a bottom tab bar, because the phone is the device this gets
 *     used on at 7am, and top navigation is out of thumb reach.
 *
 * Both are 44px-plus targets and both are keyboard reachable. The bottom bar
 * pads itself past the iPhone home indicator with the safe-area inset — this
 * app is installed to the home screen in phase 2, and without that the tab
 * labels sit under the gesture bar.
 */

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** In the bottom tab bar as well as the sidebar. */
  primary: boolean;
  end?: boolean;
}

/**
 * `/schedule` is deliberately sidebar-only: UI_KIT freezes the bottom bar at
 * four tabs (Today, Medications, History, Settings) and five is where a tab bar
 * starts getting mis-tapped. On mobile the Today screen links through to it.
 */
const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Today', icon: CalendarCheck, primary: true, end: true },
  { to: '/schedule', label: 'Schedule', icon: CalendarDays, primary: false },
  { to: '/medications', label: 'Medications', icon: Pill, primary: true },
  { to: '/history', label: 'History', icon: History, primary: true },
  { to: '/settings', label: 'Settings', icon: Settings, primary: true },
];

const PRIMARY_ITEMS = NAV_ITEMS.filter((item) => item.primary);

function PageFallback(): JSX.Element {
  return (
    <div className="flex min-h-[50dvh] items-center justify-center">
      <Spinner size="md" label="Loading page" />
    </div>
  );
}

function ThemeToggle(): JSX.Element {
  const { theme, toggle } = useTheme();
  return (
    <IconButton
      label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
      icon={theme === 'dark' ? Sun : Moon}
      variant="ghost"
      onClick={toggle}
    />
  );
}

function SignOutButton(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // A failed logout still clears the client. The cookie may survive on the
      // server, but leaving the user staring at a signed-in UI they asked to
      // leave is worse than an orphaned session row.
    }
    queryClient.clear();
    navigate('/login', { replace: true });
    toast.info('Signed out');
  }, [navigate, queryClient]);

  return (
    <IconButton
      label="Sign out"
      icon={LogOut}
      variant="ghost"
      onClick={() => {
        void signOut();
      }}
    />
  );
}

function navLinkClass(isActive: boolean): string {
  return cn(
    'flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm font-medium',
    'transition-colors duration-150 ease-out',
    'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
    isActive ? 'bg-accent/10 text-accent' : 'text-muted hover:bg-raised hover:text-ink',
  );
}

export function AppShell(): JSX.Element {
  const session = useSession();
  const userName = session.data?.user.displayName ?? '';

  return (
    <div className="min-h-dvh bg-canvas text-ink">
      {/* Keyboard users should not have to tab through the whole sidebar to
          reach the dose list. */}
      <a
        href="#main-content"
        className={cn(
          'sr-only-focusable fixed left-4 top-4 z-[100] rounded-lg bg-accent px-4 py-2',
          'text-sm font-medium text-accent-ink shadow-pop',
        )}
      >
        Skip to content
      </a>

      {/* ---------------- Sidebar, lg and up ---------------- */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-line bg-surface lg:flex',
          'pad-safe-top',
        )}
      >
        <div className="flex items-center gap-2 px-4 py-4">
          <span className="flex size-8 items-center justify-center rounded-lg bg-accent/10 text-accent">
            <Pill className="size-4" aria-hidden="true" />
          </span>
          <span className="text-sm font-semibold tracking-tight">Medication Tracker</span>
        </div>

        <div className="px-3 pb-3">
          <ProfileSwitcher variant="menu" />
        </div>

        <nav aria-label="Main" className="flex-1 space-y-1 px-3">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => navLinkClass(isActive)}
            >
              <item.icon className="size-4 shrink-0" aria-hidden="true" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center justify-between gap-2 border-t border-line px-3 py-3">
          <span className="min-w-0 flex-1 truncate text-xs text-muted" title={userName}>
            {userName}
          </span>
          <ThemeToggle />
          <SignOutButton />
        </div>
      </aside>

      {/* ---------------- Mobile top bar ---------------- */}
      <header
        className={cn(
          'sticky top-0 z-20 border-b border-line bg-canvas/85 backdrop-blur lg:hidden',
          'pad-safe-top pad-safe-x',
        )}
      >
        <div className="flex items-center gap-2 px-3 py-2">
          <div className="min-w-0 flex-1">
            <ProfileSwitcher variant="row" />
          </div>
          <ThemeToggle />
        </div>
      </header>

      {/* ---------------- Content ---------------- */}
      {/* Below lg, `pad-safe-x` owns this element's horizontal padding (it sets
          padding-left/right, which beat a `px-*` utility in the cascade), so
          main contributes only the notch inset. Each page's root supplies the
          visible gutter itself with `px-4`, as Today does; a page root without
          it renders flush against the screen edge on a phone. A `px-*` here
          would be dead below lg, so there is none. */}
      <main
        id="main-content"
        tabIndex={-1}
        className={cn(
          'mx-auto w-full max-w-3xl py-5 lg:max-w-5xl lg:px-8 lg:py-8',
          'lg:pl-[calc(16rem+2rem)]',
          // Clear the bottom tab bar (h-16) plus the home indicator.
          'pb-[calc(5rem+env(safe-area-inset-bottom,0px))] lg:pb-8',
          'pad-safe-x',
        )}
      >
        <Suspense fallback={<PageFallback />}>
          <Outlet />
        </Suspense>
      </main>

      {/* ---------------- Bottom tab bar, below lg ---------------- */}
      <nav
        aria-label="Main"
        className={cn(
          'fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 backdrop-blur lg:hidden',
          'pad-safe-bottom pad-safe-x',
        )}
      >
        <ul className="flex items-stretch justify-around">
          {PRIMARY_ITEMS.map((item) => (
            <li key={item.to} className="flex-1">
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'flex min-h-16 flex-col items-center justify-center gap-1 px-1 py-2',
                    'text-[0.6875rem] font-medium leading-4',
                    'transition-colors duration-150 ease-out',
                    'outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent',
                    isActive ? 'text-accent' : 'text-muted',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <item.icon
                      className={cn('size-5 shrink-0', isActive ? 'text-accent' : undefined)}
                      aria-hidden="true"
                    />
                    <span>{item.label}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

export default AppShell;
